import "@/App.css";
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider, useI18n } from "@/i18n";
import { AuthProvider } from "@/context/AuthContext";
import { PublicLayout } from "@/components/PublicLayout";

import Home from "@/pages/Home";
import Schools from "@/pages/Schools";
import SchoolDetail from "@/pages/SchoolDetail";
import Programmes from "@/pages/Programmes";
import ProgrammeDetail from "@/pages/ProgrammeDetail";
import About from "@/pages/About";
import EducationalModel from "@/pages/EducationalModel";
import Leadership from "@/pages/Leadership";
import Faculty from "@/pages/Faculty";
import Research from "@/pages/Research";
import Admissions from "@/pages/Admissions";
import Tuition from "@/pages/Tuition";
import FinancialAid from "@/pages/FinancialAid";
import Scholarships from "@/pages/Scholarships";
import Africa from "@/pages/Africa";
import Invest from "@/pages/Invest";
import Contact from "@/pages/Contact";
import Parents from "@/pages/Parents";
import SimplePage from "@/pages/SimplePage";
import Login from "@/pages/Login";
import StaffLogin from "@/pages/StaffLogin";
import Register from "@/pages/Register";
import Apply from "@/pages/Apply";
import ApplicationCentre from "@/pages/ApplicationCentre";
import ApplicationDetail from "@/pages/ApplicationDetail";
import Assessment from "@/pages/Assessment";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import NucleusPay from "@/pages/NucleusPay";
import SaleLinkPay from "@/pages/SaleLinkPay";
import Dashboard from "@/pages/admin/Dashboard";
import CRM from "@/pages/admin/CRM";
import AILab from "@/pages/admin/AILab";
import Appearance from "@/pages/admin/Appearance";
import Registry from "@/pages/admin/Registry";
import AdminFaculty from "@/pages/admin/Faculty";
import AdminConnect from "@/pages/admin/Connect";
import Connect from "@/pages/Connect";
import AdminContacts from "@/pages/admin/Contacts";
import AdminImports from "@/pages/admin/Imports";
import AdminPages from "@/pages/admin/Pages";
import AdminMedia from "@/pages/admin/Media";
import AdminSales from "@/pages/admin/Sales";
import AdminIntegrations from "@/pages/admin/Integrations";
import AdminCampaigns from "@/pages/admin/Campaigns";
import AdminAutomations from "@/pages/admin/Automations";
import AdminInbox from "@/pages/admin/Inbox";
import AdminAutopilot from "@/pages/admin/Autopilot";
import AdminTuition from "@/pages/admin/Tuition";
import AdminDocStudio from "@/pages/admin/DocStudio";
import AdminBranding from "@/pages/admin/Branding";
import AdminSITF from "@/pages/admin/SITF";
import GrowthStudio from "@/pages/admin/GrowthStudio";
import Laboratoire from "@/pages/Laboratoire";
import AdminSITFAssessment from "@/pages/admin/SITFAssessment";
import AdminSITFCohort from "@/pages/admin/SITFCohort";
import PortalActivate from "@/pages/portal/PortalActivate";
import PortalReset from "@/pages/portal/PortalReset";
import { PortalHome, ChildRecord, ReportViewer } from "@/pages/portal/ParentPortal";
import LandingPage from "@/pages/LandingPage";
import { RequireAdmin } from "@/pages/admin/AdminLayout";
import { RequireAuth } from "@/context/AuthContext";

const P = (C) => <PublicLayout>{C}</PublicLayout>;

function PixelPageView() {
  const loc = useLocation();
  useEffect(() => { if (window.fbq) window.fbq("track", "PageView"); }, [loc.pathname]);
  return null;
}

function SeoHead() {
  const { t, lang } = useI18n();
  useEffect(() => {
    document.title = t("seo_title");
    document.documentElement.lang = lang;
    const setMeta = (sel, attr, val) => {
      let el = document.querySelector(sel);
      if (el) el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', "content", t("seo_desc"));
    setMeta('meta[property="og:title"]', "content", t("seo_title"));
    setMeta('meta[property="og:description"]', "content", t("seo_desc"));
    setMeta('meta[name="twitter:title"]', "content", t("seo_title"));
    setMeta('meta[name="twitter:description"]', "content", t("seo_desc"));
  }, [lang, t]);
  return null;
}

function App() {
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <PixelPageView />
            <SeoHead />
            <Routes>
              <Route path="/" element={<PublicLayout transparentNav><Home /></PublicLayout>} />
              <Route path="/schools/:slug" element={P(<SchoolDetail />)} />
              <Route path="/schools" element={P(<Schools />)} />
              <Route path="/accelerated-studies" element={P(<SchoolDetail />)} />
              <Route path="/programmes" element={P(<Programmes />)} />
              <Route path="/programmes/:slug" element={P(<ProgrammeDetail />)} />
              <Route path="/about" element={P(<About />)} />
              <Route path="/mission" element={P(<About />)} />
              <Route path="/educational-model" element={P(<EducationalModel />)} />
              <Route path="/leadership" element={P(<Leadership />)} />
              <Route path="/faculty" element={P(<Faculty />)} />
              <Route path="/research" element={P(<Research />)} />
              <Route path="/student-work" element={P(<Research />)} />
              <Route path="/admissions" element={P(<Admissions />)} />
              <Route path="/assessment-centre" element={P(<Admissions />)} />
              <Route path="/tuition" element={P(<Tuition />)} />
              <Route path="/financial-aid" element={P(<FinancialAid />)} />
              <Route path="/scholarships" element={P(<Scholarships />)} />
              <Route path="/africa" element={P(<Africa />)} />
              <Route path="/international" element={P(<SimplePage slug="international" />)} />
              <Route path="/invest" element={P(<Invest />)} />
              <Route path="/invest/:section" element={P(<Invest />)} />
              <Route path="/parents" element={P(<Parents />)} />
              <Route path="/contact" element={P(<Contact />)} />
              <Route path="/governance" element={P(<SimplePage slug="governance" />)} />
              <Route path="/credentials" element={P(<SimplePage slug="credentials" />)} />
              <Route path="/recognition" element={P(<SimplePage slug="credentials" />)} />
              <Route path="/accreditation" element={P(<SimplePage slug="credentials" />)} />
              <Route path="/news" element={P(<SimplePage slug="news" />)} />
              <Route path="/events" element={P(<SimplePage slug="events" />)} />
              <Route path="/open-days" element={P(<SimplePage slug="events" />)} />
              <Route path="/privacy" element={P(<SimplePage slug="privacy" />)} />
              <Route path="/terms" element={P(<SimplePage slug="terms" />)} />
              <Route path="/accessibility" element={P(<SimplePage slug="accessibility" />)} />
              <Route path="/safeguarding" element={P(<SimplePage slug="safeguarding" />)} />

              <Route path="/apply" element={<Apply />} />
              <Route path="/centre" element={<RequireAuth><ApplicationCentre /></RequireAuth>} />
              <Route path="/centre/:appId" element={<RequireAuth><ApplicationDetail /></RequireAuth>} />
              <Route path="/apply/pay/:appId" element={<NucleusPay />} />
              <Route path="/pay/link/:slug" element={<SaleLinkPay />} />
              <Route path="/apply/tuition/:appId" element={<NucleusPay />} />
              <Route path="/assessment/:appId" element={<Assessment />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/login" element={<Login />} />
              <Route path="/staff/login" element={<StaffLogin />} />
              <Route path="/register" element={<Register />} />
              <Route path="/laboratoire" element={<Laboratoire />} />

              <Route path="/admin" element={<RequireAdmin><Dashboard /></RequireAdmin>} />
              <Route path="/admin/crm" element={<RequireAdmin><CRM /></RequireAdmin>} />
              <Route path="/admin/ai" element={<RequireAdmin><AILab /></RequireAdmin>} />
              <Route path="/admin/appearance" element={<RequireAdmin><Appearance /></RequireAdmin>} />
              <Route path="/admin/registry" element={<RequireAdmin><Registry /></RequireAdmin>} />
              <Route path="/admin/faculty" element={<RequireAdmin><AdminFaculty /></RequireAdmin>} />
              <Route path="/admin/connect" element={<RequireAdmin><AdminConnect /></RequireAdmin>} />
              <Route path="/admin/contacts" element={<RequireAdmin><AdminContacts /></RequireAdmin>} />
              <Route path="/admin/imports" element={<RequireAdmin><AdminImports /></RequireAdmin>} />
              <Route path="/admin/pages" element={<RequireAdmin><AdminPages /></RequireAdmin>} />
              <Route path="/admin/media" element={<RequireAdmin><AdminMedia /></RequireAdmin>} />
              <Route path="/admin/sales" element={<RequireAdmin><AdminSales /></RequireAdmin>} />
              <Route path="/admin/campaigns" element={<RequireAdmin><AdminCampaigns /></RequireAdmin>} />
              <Route path="/admin/growth" element={<RequireAdmin><GrowthStudio /></RequireAdmin>} />
              <Route path="/admin/integrations" element={<RequireAdmin><AdminIntegrations /></RequireAdmin>} />
              <Route path="/admin/automations" element={<RequireAdmin><AdminAutomations /></RequireAdmin>} />
              <Route path="/admin/inbox" element={<RequireAdmin><AdminInbox /></RequireAdmin>} />
              <Route path="/admin/autopilot" element={<RequireAdmin><AdminAutopilot /></RequireAdmin>} />
              <Route path="/admin/tuition" element={<RequireAdmin><AdminTuition /></RequireAdmin>} />
              <Route path="/admin/doc-studio" element={<RequireAdmin><AdminDocStudio /></RequireAdmin>} />
              <Route path="/admin/branding" element={<RequireAdmin><AdminBranding /></RequireAdmin>} />
              <Route path="/admin/sitf" element={<RequireAdmin><AdminSITF /></RequireAdmin>} />
              <Route path="/admin/sitf/cohort" element={<RequireAdmin><AdminSITFCohort /></RequireAdmin>} />
              <Route path="/admin/sitf/:aid" element={<RequireAdmin><AdminSITFAssessment /></RequireAdmin>} />
              <Route path="/portal/login" element={<Navigate to="/login" replace />} />
              <Route path="/portal/activate" element={<PortalActivate />} />
              <Route path="/portal/reset" element={<PortalReset />} />
              <Route path="/portal" element={<PortalHome />} />
              <Route path="/portal/child/:cid" element={<ChildRecord />} />
              <Route path="/portal/child/:cid/report/:aid" element={<ReportViewer />} />
              <Route path="/connect" element={<Connect />} />
              <Route path="/p/:slug" element={P(<LandingPage />)} />

              {/* Any unknown / legacy Kajabi URL lands on the homepage instead of a blank page */}
              <Route path="*" element={<PublicLayout transparentNav><Home /></PublicLayout>} />
            </Routes>
            <Toaster position="top-right" />
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
